import { forwardRef } from 'react';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { ScrollTopButtonMui } from '../ScrollTopButtonMui';
import { BackstagePage } from './BackstagePage';
import { ContentMui as Content } from './ContentMui';
import { Header } from './Header';
import { HeaderContentMui as HeaderContent } from './HeaderContentMui';
import { HeaderTabsContainerMui as HeaderTabsContainer } from './HeaderTabsContainerMui';
import type { PageLayoutProps } from './PageLayout';
import { PageTitle } from './PageTitle';
import { SidebarContentMui as SidebarContent } from './SidebarContentMui';
import { hasPageLayoutHeader } from './hasPageLayoutHeader';

const useStyles = makeStyles(theme => ({
  content: { paddingBottom: theme.spacing(12) },
  // min-height keeps the pageContent fr track from growing past the page —
  // fr tracks have an auto minimum, which would force the page to scroll.
  fullViewportContent: {
    minHeight: 0,
    overflow: 'hidden',
    paddingBottom: theme.spacing(4),
  },
  scrollableFullViewportContent: {
    overflow: 'auto',
  },
}));

export const PageLayoutMui = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      pageHeader,
      headerContent,
      stickyHeaderTabs = true,
      scrollButton = false,
      narrow,
      noPadding,
      fullViewport,
      contentOverflow = 'hidden',
      ...headerProps
    },
    ref,
  ) => {
    const classes = useStyles();

    const hasHeader = hasPageLayoutHeader({
      ...headerProps,
      headerContent,
    });

    return (
      <BackstagePage ref={ref} fullViewport={fullViewport}>
        {floatingHeader}
        {scrollButton && <ScrollTopButtonMui />}
        {pageHeader ??
          (hasHeader ? (
            <Header
              {...headerProps}
              narrow={narrow}
              stickyHeaderTabs={stickyHeaderTabs}
            >
              {headerContent ? (
                <HeaderContent>{headerContent}</HeaderContent>
              ) : null}
            </Header>
          ) : (
            headerProps.pageTitleOverride && (
              <PageTitle title={headerProps.pageTitleOverride} />
            )
          ))}
        {pageHeader && headerProps.headerTabs && (
          <HeaderTabsContainer
            sticky={stickyHeaderTabs}
            narrow={narrow}
            ref={headerProps.headerTabsRef}
          >
            {headerProps.headerTabs}
          </HeaderTabsContainer>
        )}

        <Content
          narrow={narrow}
          className={classNames(noPadding ? undefined : classes.content, {
            [classes.fullViewportContent]: fullViewport,
            [classes.scrollableFullViewportContent]:
              fullViewport && contentOverflow === 'auto',
          })}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
PageLayoutMui.displayName = 'PageLayoutMui';
